import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import instance from "../../../Interceptor/api_instance";
import { Spinner } from "../../../Components/LoadingAnimations/LoadingAnimations";
import TrailerSearch from "../../../Components/Inputs/TrailerSearch";
import { ActionResponse } from "../../../Components/Modals/ActionResponses";
import LocationSearch from "../../../Components/Inputs/LocationSearch";
import NewTruckSearchSelect from "../../../Components/Inputs/NewTruckSearch";

export const NewMaintenanceReport = ({ close }) => {
  const { t } = useTranslation();
  const [vehicleType, setVehicleType] = useState("truck");
  const [carrier, setCarrier] = useState("jfg");
  const [truck, setTruck] = useState(null);
  const navigate = useNavigate();
  const [trailer, setTrailer] = useState(null);
  const [location, setLocation] = useState(null);
  const [assignedSpot, setAssignedSpot] = useState(null);
  const [notes, setNotes] = useState("");
  const [odometer, setOdometer] = useState("");
  const [requestLoading, setRequestLoading] = useState(false);
  const [responseModal, setResponseModal] = useState(null);
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        vehicle_type: vehicleType,
        vehicle_id:
          vehicleType === "truck"
            ? truck.value
            : vehicleType === "trailer"
            ? trailer.value
            : null,
        location: location.value,
        assigned_spot: assignedSpot,
        notes: notes,
        odometer: odometer ? parseInt(odometer) : null,
      };
      setRequestLoading(true);
      const response = await instance.post(
        "safety/create-maintenance-report",
        data
      );
      if (response.status === 201) {
        navigate(`/maintenance-reports/${response.data.id}`);
      }
    } catch (error) {
      setResponseModal({
        message: error,
        status: "Error",
      });
    } finally {
      setRequestLoading(false);
    }
  };

  return (
    <div className="page-mask page-mask-animation">
      {responseModal && (
        <ActionResponse
          status={responseModal.status}
          message={responseModal.message}
          close={() => setResponseModal(null)}
        />
      )}
      <div className="new-report-cont">
        <h4 data-testid="backlog row" className="global-h2">
          {t("forms.type.maintenance-report.new")}
        </h4>
        <form onSubmit={handleSubmit}>
          {/* Form group: vehicle type */}
          <div className="form-group">
            <label className="input-label required">
              {t("forms.type.maintenance-report.labels.vehicle-type")}
            </label>
            <div className="input-cont">
              <fieldset className="fieldsets">
                <div className="radio-options">
                  <input
                    type="radio"
                    name="vehicleType"
                    data-testid="truckRadioBtn"
                    className="radio-input"
                    checked={vehicleType === "truck"}
                    onChange={() => setVehicleType("truck")}
                    required
                  />
                  <label>{t("yard-tables.table-columns.truck")}</label>
                </div>
                <div className="radio-options">
                  <input
                    type="radio"
                    name="vehicleType"
                    data-testid="truckRadioBtn"
                    className="radio-input"
                    checked={vehicleType === "trailer"}
                    onChange={() => setVehicleType("trailer")}
                  />
                  <label>{t("yard-tables.table-columns.trailer")}</label>
                </div>
              </fieldset>
            </div>
          </div>
          {vehicleType === "truck" && (
            <>
              {/* Form group: select carrier */}
              <div className="form-group">
                <label className="input-label required">
                  {t("yard-tables.table-columns.carrier")}
                </label>
                <div className="input-cont">
                  <select
                    value={carrier}
                    onChange={(e) => {
                      setCarrier(e.target.value);
                      setTruck({});
                    }}
                    required
                  >
                    <option value="jfg">JFG</option>
                    <option value="ati">ATI</option>
                    <option value="mfs">Mayoral</option>
                  </select>
                </div>
              </div>
              {/* Form group: JFG Trucks list */}
              <div className="form-group">
                <label className="input-label required">
                  {t("yard-tables.table-columns.truck")}
                </label>
                <div className="input-cont">
                  <NewTruckSearchSelect
                    onSelect={setTruck}
                    carrier={carrier}
                    selectedTruck={truck}
                    enableAll={true}
                  />
                </div>
              </div>
              {/* Form group: Odometer */}
              <div className="form-group">
                <label className="input-label">
                  {t("forms.type.vehicle.input-headers.odometer")}
                </label>
                <div className="input-cont">
                  <input
                    type="number"
                    className="text-input"
                    placeholder={t("forms.type.vehicle.input-headers.odometer")}
                    value={odometer}
                    onChange={(e) => setOdometer(e.target.value)}
                  />
                </div>
              </div>
            </>
          )}
          {/* Form group: trailers list */}
          {vehicleType === "trailer" && (
            <div className="form-group">
              <label className="input-label required">
                {t("yard-tables.table-columns.trailer")}
              </label>
              <div className="input-cont">
                <TrailerSearch
                  onSelect={setTrailer}
                  selectedTrailer={trailer}
                  enableAll={true}
                />
              </div>
            </div>
          )}

          <div className="form-group">
            <label className="input-label required">
              {t("yard-tables.table-columns.location")}
            </label>
            <div className="input-cont">
              <LocationSearch
                selectedOption={location}
                onSelect={(option) => setLocation(option)}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="input-label">
              {t("general.spot")} ({t("general.optional")})
            </label>
            <div className="input-cont">
              <input
                type="text"
                className="text-input"
                placeholder={t(
                  "general.spot"
                )}
                value={assignedSpot}
                onChange={(e) => setAssignedSpot(e.target.value)}
              />
            </div>
          </div>

          {/* Form group: Notes */}
          <div className="form-group">
            <label className="input-label">
              {t("forms.type.vehicle.input-headers.notes")} (
              {t("general.optional")}:{" "}
              {t("forms.type.maintenance-report.labels.guard-notes")})
            </label>
            <div className="input-cont">
              <textarea
                type="text"
                className="text-input"
                placeholder={t("forms.type.vehicle.placeholders.notes")}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>
          {requestLoading ? (
            <Spinner />
          ) : (
            <div className="full-cont">
              <button className="change-pwd-btn" type="submit">
                {t("buttons.submit")}
              </button>
              <button className="cancel-btn" type="button" onClick={close}>
                {t("buttons.close")}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};
